import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { Calendar, Hash, Layers, ShieldCheck, Sparkles } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PromotionFormData, Rule, Action } from '@/components/form/discount/promotion-form'
import { PromotionDetails } from '@/lib/api/promotions'
import { formatMoney } from '@/lib/money'
import { DateDisplay } from '@/components/date-display'

interface PromotionSummaryProps {
    promotion?: NonNullable<PromotionDetails>
}

export const PromotionSummary = ({ promotion }: PromotionSummaryProps) => {
    const { t } = useTranslation('stores')
    const { watch } = useFormContext<PromotionFormData>()

    const name = watch('name')
    const enabled = watch('enabled')
    const automatic = watch('automatic')
    const startDate = watch('startDate')
    const endDate = watch('endDate')
    const usageLimit = watch('usageLimit')
    const oncePerCustomer = watch('oncePerCustomer')
    const forceCombine = watch('forceCombine')
    const combinesWithOrderLevelDiscounts = watch('combinesWithOrderLevelDiscounts')
    const combinesWithLineItemDiscounts = watch('combinesWithLineItemDiscounts')
    const combinesWithShippingDiscounts = watch('combinesWithShippingDiscounts')
    const ruleOperator = watch('ruleOperator')
    const rules = watch('rules')
    const actions = watch('actions')

    const statusBadge = (() => {
        if (promotion?.archived) {
            return <Badge variant="secondary">{t('promotion.status.archived')}</Badge>
        }
        if (!enabled) {
            return <Badge variant="secondary">{t('promotion.status.disabled')}</Badge>
        }
        if (endDate && new Date(endDate) < new Date()) {
            return <Badge variant="secondary">{t('promotion.status.expired')}</Badge>
        }
        if (startDate && new Date(startDate) > new Date()) {
            return <Badge variant="outline">{t('promotion.status.scheduled')}</Badge>
        }
        return <Badge variant="default">{t('promotion.status.active')}</Badge>
    })()

    const scheduleLine = (() => {
        if (startDate && endDate) {
            return (
                <span className="flex flex-wrap items-baseline gap-1">
                    <DateDisplay value={startDate} mode="date" />
                    <span>→</span>
                    <DateDisplay value={endDate} mode="date" />
                </span>
            )
        }
        if (startDate) {
            return (
                <span className="flex flex-wrap items-baseline gap-1">
                    <span>{t('promotion.summary.fromLabel')}</span>
                    <DateDisplay value={startDate} mode="date" />
                </span>
            )
        }
        if (endDate) {
            return (
                <span className="flex flex-wrap items-baseline gap-1">
                    <span>{t('promotion.summary.untilLabel')}</span>
                    <DateDisplay value={endDate} mode="date" />
                </span>
            )
        }
        return t('promotion.summary.noDateLimit')
    })()

    const combineFlags: string[] = []
    if (forceCombine) combineFlags.push(t('promotion.fields.forceCombine'))
    if (combinesWithOrderLevelDiscounts) combineFlags.push(t('promotion.fields.combinesWithOrderDiscounts'))
    if (combinesWithLineItemDiscounts) combineFlags.push(t('promotion.fields.combinesWithLineItemDiscounts'))
    if (combinesWithShippingDiscounts) combineFlags.push(t('promotion.fields.combinesWithShippingDiscounts'))

    return (
        <Card>
            <CardHeader>
                <div className="flex items-start justify-between gap-2">
                    <CardTitle>{t('promotion.summary.title')}</CardTitle>
                    {statusBadge}
                </div>
            </CardHeader>
            <CardContent className="space-y-5 text-sm">
                <div className="space-y-1">
                    <div className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                        {t('promotion.fields.name')}
                    </div>
                    <div className="break-words font-medium">
                        {name || t('promotion.summary.unnamed')}
                    </div>
                    {automatic && (
                        <Badge variant="outline" className="mt-1">
                            <Sparkles className="mr-1 h-3 w-3" />
                            {t('promotion.summary.automatic')}
                        </Badge>
                    )}
                </div>

                <SummaryRow icon={<Calendar className="h-3.5 w-3.5" />} label={t('promotion.summary.schedule')}>
                    {scheduleLine}
                </SummaryRow>

                <SummaryRow icon={<Hash className="h-3.5 w-3.5" />} label={t('promotion.fields.usageLimit')}>
                    {usageLimit
                        ? t('promotion.summary.usageLimitCount', { count: usageLimit })
                        : t('promotion.fields.unlimited')}
                </SummaryRow>

                {oncePerCustomer && (
                    <SummaryRow icon={<ShieldCheck className="h-3.5 w-3.5" />} label={t('promotion.summary.perCustomer')}>
                        {t('promotion.fields.oncePerCustomer')}
                    </SummaryRow>
                )}

                {combineFlags.length > 0 && (
                    <SummaryRow icon={<Layers className="h-3.5 w-3.5" />} label={t('promotion.settings.combinationSettings')}>
                        <ul className="space-y-0.5">
                            {combineFlags.map((flag) => (
                                <li key={flag} className="text-muted-foreground">
                                    {flag}
                                </li>
                            ))}
                        </ul>
                    </SummaryRow>
                )}

                <div className="space-y-2 border-t pt-4">
                    <div className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                        {t('promotion.rules.title')}
                    </div>
                    {rules.length === 0 ? (
                        <p className="text-muted-foreground text-xs">
                            {t('promotion.summary.noRules')}
                        </p>
                    ) : (
                        <div className="space-y-1.5">
                            {rules.map((rule, index) => (
                                <React.Fragment key={index}>
                                    <div className="flex items-start gap-2">
                                        <Badge variant="outline" className="shrink-0">
                                            {getRuleLabel(rule.type, t)}
                                        </Badge>
                                        <span className="text-muted-foreground break-words">{getRuleDetail(rule, t)}</span>
                                    </div>
                                    {index < rules.length - 1 && (
                                        <div className="flex items-center gap-2">
                                            <div className="bg-border h-px flex-1" />
                                            <span className="text-muted-foreground text-[10px] font-semibold uppercase tracking-wider">
                                                {ruleOperator === 'AND'
                                                    ? t('promotion.summary.operatorAnd')
                                                    : t('promotion.summary.operatorOr')}
                                            </span>
                                            <div className="bg-border h-px flex-1" />
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>

                <div className="space-y-2 border-t pt-4">
                    <div className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                        {t('promotion.promotionActions.title')}
                    </div>
                    {actions.length === 0 ? (
                        <p className="text-muted-foreground text-xs">
                            {t('promotion.summary.noActions')}
                        </p>
                    ) : (
                        <ul className="space-y-1.5">
                            {actions.map((action, index) => (
                                <li key={index} className="flex items-start gap-2">
                                    <Badge variant="outline" className="shrink-0">
                                        {getActionLabel(action.type, t)}
                                    </Badge>
                                    <span className="text-muted-foreground break-words">{getActionDetail(action, t)}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

            </CardContent>
        </Card>
    )
}

const SummaryRow = ({
    icon,
    label,
    children,
}: {
    icon: React.ReactNode
    label: string
    children: React.ReactNode
}) => (
    <div className="space-y-1">
        <div className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide">
            {icon}
            {label}
        </div>
        <div className="break-words">{children}</div>
    </div>
)

const getRuleLabel = (
    type: Rule['type'],
    t: (key: string, opts?: Record<string, unknown>) => string,
): string => t(`promotion.rules.titles.${type}`)

const getRuleDetail = (
    rule: Rule,
    t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
    switch (rule.type) {
        case 'ContainsProduct':
            return t('promotion.summary.detail.minQuantity', { count: rule.minimumQuantity ?? 1 })
        case 'OrderAmountMinimum':
            return formatMoneyDetail(rule.minimumAmount)
        case 'OrderMinQuantity':
            return t('promotion.summary.detail.minQuantity', { count: rule.orderMinQuantity ?? 1 })
        case 'OrderMaxQuantity':
            return t('promotion.summary.detail.maxQuantity', { count: rule.orderMaxQuantity ?? 1 })
        case 'MaxProductQuantity':
            return t('promotion.summary.detail.maxQuantity', { count: rule.maximumQuantity ?? 1 })
        case 'PaymentIntegrationSelected':
            return rule.paymentMethodName
                ? rule.paymentMethodName.charAt(0).toUpperCase() + rule.paymentMethodName.slice(1)
                : ''
        case 'DoNotContainRecurringProduct':
            return ''
        case 'MaxAmount':
            return formatMoneyDetail(rule.maximumAmount)
        case 'ExcludeProduct':
            return t('promotion.summary.detail.excludedProductCount', {
                count: rule.excludedProductIds?.length ?? 0,
            })
        default:
            return ''
    }
}

const getActionLabel = (
    type: Action['type'],
    t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
    switch (type) {
        case 'LineItemAdjustment':
            return t('promotion.promotionActions.types.productDiscount')
        case 'OrderAdjustment':
            return t('promotion.promotionActions.types.orderDiscount')
        case 'ShippingAdjustment':
            return t('promotion.promotionActions.types.freeShipping')
        case 'CreateLineItem':
            return t('promotion.promotionActions.types.addFreeProduct')
        case 'SubscriptionLineItemFreeTrial':
            return t('promotion.promotionActions.types.freeTrial')
        case 'BOGO':
            return t('promotion.promotionActions.types.bogo')
        default:
            return type
    }
}

const getActionDetail = (
    action: Action,
    t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
    switch (action.type) {
        case 'LineItemAdjustment':
        case 'OrderAdjustment':
            if (action.adjustmentType === 'percentage') {
                return `${action.adjustmentPercentage ?? 0}%`
            }
            if (action.adjustmentType === 'amount') {
                return formatMoneyDetail(action.adjustmentAmount)
            }
            return ''
        case 'ShippingAdjustment':
            return t('promotion.promotionActions.types.freeShipping')
        case 'CreateLineItem':
            return t('promotion.summary.detail.quantity', { count: action.quantityToAdd ?? 1 })
        case 'SubscriptionLineItemFreeTrial': {
            const unit = action.subscriptionFreeTrialDurationType ?? 'day'
            return t(`promotion.promotionActions.durationTypes.${unit}`) +
                ` × ${action.subscriptionFreeTrialDuration ?? 0}`
        }
        case 'BOGO':
            return t('promotion.summary.detail.bogo', {
                buy: action.buyQuantityThreshold ?? 1,
                get: action.quantityToAdd ?? 1,
            })
        default:
            return ''
    }
}

const formatMoneyDetail = (currencyOptions?: Record<string, { amount: number }> | null): string => {
    if (!currencyOptions) return ''
    const entries = Object.entries(currencyOptions).filter(([, value]) => value && value.amount > 0)
    if (entries.length === 0) return ''
    const [code, value] = entries[0]
    const formatted = formatMoney(value.amount, code)
    if (entries.length === 1) return formatted
    return `${formatted} +${entries.length - 1}`
}
